import React, { useCallback, useEffect, useMemo, useState } from "react";
import AiFeatureToolPrep from "../../shared/Popup/AiFeatureToolPrep";
import AiPresetCards from "../../shared/Popup/AiPresetCards";
import { presetTip } from "../../shared/Popup/presetTip";
import { shadowSingle } from "../../../../../services/api/tools/studio_features";
import ShadowGlobe, { LIGHT_DIRECTION_PRESETS } from "./ShadowGlobe";
import AiShadowLivePreview from "./AiShadowLivePreview";
import "./AiShadowToolPrep.css";

const SHADOW_STYLES = [
  {
    id: "daylight",
    label: "Daylight",
    sub: "Long outdoor cast",
    tip: presetTip('Daylight — "Long outdoor cast"', [
      "Longer shadow like outdoor sun",
      "Good default for product shots",
      "Slightly softer edge than Crisp",
    ]),
    globeAuto: false,
    poseDeg: 55,
    directionDeg: 225,
    intensity: 70,
    softness: 55,
  },
  {
    id: "soft_light",
    label: "Soft light",
    sub: "Gentle and diffused",
    tip: presetTip('Soft light — "Gentle and diffused"', [
      "Soft, low-contrast shadow",
      "Natural look for most products",
      "Lowest chance of harsh edges",
    ]),
    globeAuto: false,
    poseDeg: 55,
    directionDeg: 225,
    intensity: 50,
    softness: 85,
  },
  {
    id: "crisp",
    label: "Crisp",
    sub: "Sharp graphic edge",
    tip: presetTip('Crisp — "Sharp graphic edge"', [
      "Defined shadow with sharper falloff",
      "Stronger sense of light direction",
      "Good for dramatic studio looks",
    ]),
    globeAuto: false,
    poseDeg: 55,
    directionDeg: 225,
    intensity: 80,
    softness: 15,
  },
  {
    id: "floating",
    label: "Floating",
    sub: "Soft pool underneath",
    tip: presetTip('Floating — "Soft pool underneath"', [
      "Soft contact shadow under the subject",
      "Makes cutouts feel lightly lifted",
      "Ideal for floating product shots",
    ]),
    globeAuto: false,
    poseDeg: 80,
    directionDeg: 180,
    intensity: 55,
    softness: 80,
  },
];

const POSE_OPTIONS = [
  { id: "upright", label: "Upright" },
  { id: "flatlay", label: "Flat lay" },
];

const STYLE_PREVIEW = {
  daylight: { blur: 14, opacityMul: 1, stretch: 2.2 },
  soft_light: { blur: 36, opacityMul: 0.75, stretch: 1.35, wide: true },
  crisp: { blur: 3, opacityMul: 1.15, stretch: 1.9 },
  floating: { blur: 22, opacityMul: 0.85, stretch: 0.75, contactBias: true },
};

/** UI style ids → PhotoStudio soft / hard / contact / floating. */
const STYLE_API = {
  daylight: "soft",
  soft_light: "soft",
  crisp: "hard",
  floating: "floating",
};

function mapIntensity(value) {
  if (value < 34) return "light";
  if (value <= 66) return "medium";
  return "strong";
}

/**
 * Photoroom compass → API cardinals.
 * 0° = behind (top of disc), 90° = left, 180° = front, 270° = right.
 */
function mapDirection(deg) {
  const d = ((Number(deg) % 360) + 360) % 360;
  const buckets = [
    { name: "back", angle: 0 },
    { name: "left", angle: 90 },
    { name: "front", angle: 180 },
    { name: "right", angle: 270 },
  ];
  let best = buckets[0];
  let bestDist = Infinity;
  for (const b of buckets) {
    let dist = Math.abs(d - b.angle);
    if (dist > 180) dist = 360 - dist;
    if (dist < bestDist) {
      bestDist = dist;
      best = b;
    }
  }
  return best.name;
}

function globeHint(auto, directionDeg, poseDeg) {
  if (auto) {
    return "Auto — picks light direction and elevation for you.";
  }
  return `Light at ${directionDeg}° · elevation ${poseDeg}°. Drag the marker or use a preset — cast stays pinned at the feet.`;
}

/**
 * Live canvas cast pinned to the subject's feet.
 * Auto still previews the style's default aim — it only skips sending
 * direction/pose to the API on Generate.
 */
function buildLivePreviewCast({
  directionDeg,
  poseDeg,
  intensity,
  softness,
  styleId,
}) {
  const style = STYLE_PREVIEW[styleId] || STYLE_PREVIEW.daylight;
  const intensity01 = Math.max(0.2, Math.min(1, intensity / 100));
  const softness01 = Math.max(0, Math.min(1, Number(softness) / 100));

  const dir = directionDeg;
  const pose = poseDeg;

  const screenDeg = (((-90 - dir) % 360) + 360) % 360;
  const rad = (screenDeg * Math.PI) / 180;
  const lightUx = Math.cos(rad);
  const lightUy = Math.sin(rad);

  let castUx = -lightUx;
  let castUy = -lightUy;
  if (castUy < 0.2) castUy = 0.2 + Math.max(0, -castUy) * 0.3;
  const n = Math.hypot(castUx, castUy) || 1;
  castUx /= n;
  castUy /= n;

  // Elevation: 90° overhead → short; 0° low → long.
  const elevNorm = Math.max(0, Math.min(1, pose / 90));
  const poseMul = 0.45 + (1 - elevNorm) * 1.35;
  let lengthScale = (1.1 + poseMul) * (0.85 + intensity01 * 0.45);
  if (style.contactBias) {
    lengthScale = 0.65 + (1 - elevNorm) * 0.35 + intensity01 * 0.1;
  } else if (style.wide) {
    lengthScale = Math.max(1.2, lengthScale * 0.88);
  }

  const rawAngle = (Math.atan2(castUy, castUx) * 180) / Math.PI;
  const angleDeg = style.contactBias ? rawAngle * 0.4 : rawAngle;

  return {
    offsetX: 0,
    offsetY: 0,
    blur: Math.round((2 + softness01 * 36) * (0.85 + intensity01 * 0.3)),
    opacity: Math.min(0.95, style.opacityMul * (0.55 + intensity01 * 0.4)),
    angleDeg,
    scaleX: Number((lengthScale * (style.stretch || 1.15)).toFixed(3)),
    scaleY: Number(
      (
        style.contactBias
          ? 0.72 + elevNorm * 0.1
          : style.wide
            ? 0.68
            : 0.4 + elevNorm * 0.12
      ).toFixed(3)
    ),
    contact: Boolean(style.contactBias),
  };
}

function SliderRow({
  label,
  value,
  min,
  max,
  step = 1,
  disabled,
  suffix = "",
  onChange,
  ariaLabel,
  ends = null,
}) {
  const [draft, setDraft] = useState(String(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(String(value));
  }, [value, focused]);

  const fillPct = ((value - min) / (max - min || 1)) * 100;
  const sliderBg = `linear-gradient(to right, #4f46e5 0%, #4f46e5 ${fillPct}%, #2a2a2a ${fillPct}%, #2a2a2a 100%)`;

  const commitDraft = () => {
    const parsed = Number.parseInt(String(draft).replace(/[^\d-]/g, ""), 10);
    if (!Number.isFinite(parsed)) {
      setDraft(String(value));
      return;
    }
    const next = Math.max(min, Math.min(max, parsed));
    setDraft(String(next));
    if (next !== value) onChange(next);
  };

  return (
    <div className="astp-slider-block">
      <div className="astp-slider-head">
        <p className="aftp-label astp-slider-label">{label}</p>
        <div className="astp-slider-value-wrap">
          <input
            type="text"
            inputMode="numeric"
            className="astp-slider-input"
            value={draft}
            disabled={disabled}
            aria-label={`${ariaLabel} value`}
            onFocus={() => setFocused(true)}
            onChange={(e) => {
              const next = e.target.value.replace(/[^\d]/g, "");
              setDraft(next);
            }}
            onBlur={() => {
              setFocused(false);
              commitDraft();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.currentTarget.blur();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setDraft(String(value));
                e.currentTarget.blur();
              }
            }}
          />
          {suffix ? (
            <span className="astp-slider-suffix" aria-hidden="true">
              {suffix}
            </span>
          ) : null}
        </div>
      </div>
      <input
        type="range"
        className="astp-slider"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        aria-label={ariaLabel}
        style={{ "--astp-slider-bg": sliderBg }}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      {ends ? (
        <div className="astp-slider-ends" aria-hidden="true">
          <span>{ends[0]}</span>
          <span>{ends[1]}</span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * AI Tools → AI Shadow prep.
 */
const AiShadowToolPrep = (props) => {
  const handleOpenDone = useCallback(
    (result) => {
      if (typeof props.onDone !== "function") return undefined;
      return props.onDone({ ...result, preserveShadowLayout: true });
    },
    [props.onDone]
  );
  const defaultStyle = SHADOW_STYLES[0];
  const [styleId, setStyleId] = useState(defaultStyle.id);
  const [globeAuto, setGlobeAuto] = useState(defaultStyle.globeAuto);
  const [poseDeg, setPoseDeg] = useState(defaultStyle.poseDeg);
  const [directionDeg, setDirectionDeg] = useState(defaultStyle.directionDeg);
  const [intensity, setIntensity] = useState(defaultStyle.intensity);
  const [softness, setSoftness] = useState(defaultStyle.softness);
  const [pose, setPose] = useState("upright");
  const [whitePreview, setWhitePreview] = useState(false);
  const [apiAim, setApiAim] = useState(null);

  const applyStyle = (id) => {
    const style = SHADOW_STYLES.find((s) => s.id === id);
    if (!style) return;
    setStyleId(id);
    setGlobeAuto(style.globeAuto);
    setPoseDeg(style.poseDeg);
    setDirectionDeg(style.directionDeg);
    setIntensity(style.intensity);
    setSoftness(style.softness);
    setApiAim(null);
  };

  const applyLightPreset = (preset) => {
    setGlobeAuto(false);
    setDirectionDeg(preset.directionDeg);
    setPoseDeg(preset.poseDeg);
    setApiAim({ direction: preset.apiDirection });
  };

  const activePresetId = useMemo(() => {
    if (globeAuto) return null;
    const hit = LIGHT_DIRECTION_PRESETS.find(
      (p) =>
        Math.abs(p.directionDeg - directionDeg) <= 12 &&
        Math.abs(p.poseDeg - poseDeg) <= 12
    );
    return hit?.id ?? null;
  }, [globeAuto, directionDeg, poseDeg]);

  const hint = useMemo(
    () => globeHint(globeAuto, directionDeg, poseDeg),
    [globeAuto, directionDeg, poseDeg]
  );

  const liveCast = useMemo(
    () =>
      buildLivePreviewCast({
        directionDeg,
        poseDeg,
        intensity,
        softness,
        styleId,
      }),
    [directionDeg, poseDeg, intensity, softness, styleId]
  );

  const handleGenerate = useCallback(
    async ({ token, file, image_url, removeBg }) => {
      const opts = {
        style: STYLE_API[styleId] || "soft",
        intensity: mapIntensity(intensity),
        softness,
        pose,
        elevation: poseDeg,
        direction: apiAim?.direction || mapDirection(directionDeg),
        direction_deg: directionDeg,
        remove_background_first: Boolean(removeBg),
        output_format: "png",
        ...(image_url ? { image_url } : {}),
      };
      return shadowSingle(token, file, opts);
    },
    [styleId, intensity, softness, pose, poseDeg, directionDeg, apiAim]
  );

  return (
    <AiFeatureToolPrep
      {...props}
      onDone={handleOpenDone}
      onGenerate={handleGenerate}
      generateFileName="shadow-input.png"
      toolKey="ai_shadow"
      title="AI Shadow"
      aka="AI Drop Shadow"
      bio="Add a natural drop shadow so products feel grounded and dimensional."
      emptyPreviewLabel="Upload an image to preview shadow"
      loadingLabel="Adding shadow…"
      emptyFooterHint="Upload an image, pick a shadow style, then generate."
      removeBgDefault
      removeBgBio="Recommended — shadows look best on a clean cutout."
      renderPreview={(ctx) => {
        const useLive =
          !ctx.hasResult && Boolean(ctx.imageUrl) && !ctx.loading;
        return (
          <AiShadowLivePreview
            imageUrl={ctx.imageUrl}
            loading={ctx.loading}
            loadingLabel={ctx.loadingLabel}
            emptyLabel={ctx.emptyLabel}
            transparent={ctx.transparent}
            whiteBackground={whitePreview}
            showLiveShadow={useLive}
            liveCast={useLive ? liveCast : null}
            showSubjectBox={Boolean(
              ctx.showSubjectBox ??
                (ctx.imageUrl && (ctx.transparent || ctx.removeBg))
            )}
          />
        );
      }}
      renderControls={({
        busy,
        leftPanelRef,
        sourceImageUrl,
        cutoutUrl,
        bgLoading,
      }) => (
        <>
          <div className="aftp-section">
            <p className="aftp-label">Shadow style</p>
            <AiPresetCards
              presets={SHADOW_STYLES}
              selectedId={styleId}
              columns={2}
              ariaLabel="Shadow style"
              boundaryRef={leftPanelRef}
              disabled={busy}
              onSelect={(id) => {
                applyStyle(id);
              }}
            />
          </div>

          <div className="aftp-section">
            <p className="aftp-label">Subject pose</p>
            <div className="astp-pose-presets" role="group" aria-label="Subject pose">
              {POSE_OPTIONS.map((option) => {
                const selected = pose === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`astp-light-preset${selected ? " is-selected" : ""}`}
                    aria-pressed={selected}
                    disabled={busy}
                    onClick={() => {
                      setPose(option.id);
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="aftp-section">
            <p className="aftp-label">Light direction</p>
            <ShadowGlobe
              directionDeg={directionDeg}
              poseDeg={poseDeg}
              auto={globeAuto}
              disabled={busy}
              size={288}
              sourceImageUrl={cutoutUrl || null}
              subjectLoading={Boolean(sourceImageUrl && !cutoutUrl && bgLoading)}
              styleId={styleId}
              intensity={intensity}
              onChange={({ directionDeg: d, poseDeg: p }) => {
                setGlobeAuto(false);
                setApiAim(null);
                setDirectionDeg(d);
                setPoseDeg(p);
              }}
              onAutoToggle={(next) => {
                setGlobeAuto(next);
                setApiAim(null);
              }}
            />
            <p className="astp-hint">{hint}</p>

            <div
              className="astp-light-presets"
              role="group"
              aria-label="Light direction presets"
            >
              {LIGHT_DIRECTION_PRESETS.map((preset) => {
                const selected = activePresetId === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    className={`astp-light-preset${selected ? " is-selected" : ""}`}
                    aria-pressed={selected}
                    disabled={busy}
                    onClick={() => {
                      applyLightPreset(preset);
                    }}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="aftp-section">
            <SliderRow
              label="Direction"
              value={directionDeg}
              min={0}
              max={359}
              disabled={busy || globeAuto}
              suffix="°"
              ariaLabel="Light direction degrees"
              ends={["0°", "359°"]}
              onChange={(next) => {
                setGlobeAuto(false);
                setApiAim(null);
                setDirectionDeg(((next % 360) + 360) % 360);
              }}
            />
          </div>

          <div className="aftp-section">
            <SliderRow
              label="Elevation"
              value={poseDeg}
              min={0}
              max={90}
              disabled={busy || globeAuto}
              suffix="°"
              ariaLabel="Light elevation degrees"
              ends={["Low", "Overhead"]}
              onChange={(next) => {
                setGlobeAuto(false);
                setApiAim(null);
                setPoseDeg(Math.max(0, Math.min(90, next)));
              }}
            />
          </div>

          <div className="aftp-section">
            <p className="aftp-label">Preview background</p>
            <div className="astp-pose-presets" role="group" aria-label="Preview background">
              <button
                type="button"
                className={`astp-light-preset${whitePreview ? "" : " is-selected"}`}
                aria-pressed={!whitePreview}
                disabled={busy}
                onClick={() => setWhitePreview(false)}
              >
                Checkerboard
              </button>
              <button
                type="button"
                className={`astp-light-preset${whitePreview ? " is-selected" : ""}`}
                aria-pressed={whitePreview}
                disabled={busy}
                onClick={() => setWhitePreview(true)}
              >
                White
              </button>
            </div>
            <p className="astp-hint">White is preview only. Download keeps the transparent shadow.</p>
          </div>

          <div className="aftp-section">
            <SliderRow
              label="Intensity"
              value={intensity}
              min={0}
              max={100}
              disabled={busy}
              suffix="%"
              ariaLabel="Shadow intensity"
              ends={["Light", "Dark"]}
              onChange={(next) => {
                setIntensity(next);
              }}
            />
          </div>

          <div className="aftp-section">
            <SliderRow
              label="Softness"
              value={softness}
              min={0}
              max={100}
              disabled={busy}
              suffix="%"
              ariaLabel="Shadow softness"
              ends={["Hard", "Soft"]}
              onChange={(next) => {
                setSoftness(next);
              }}
            />
          </div>
        </>
      )}
    />
  );
};

export default AiShadowToolPrep;
